import React from 'react';

// Backend-aligned defaults (used as fallback if API hasn't loaded yet)
export const DEFAULT_PAYMENT_TERMS = {
  mobilization_advance_pct: 10,
  material_advance_pct: 5,
  advance_bg_pct: 110,
  performance_bg_pct: 5,
  retention_pct: 5,
  dlp_months: 12,
  ra_certification_days: 7,
  bill_processing_days: 30,
  ld_clause: '0.5% per week up to 10%',
};

const NUMERIC_FIELDS = [
  { k: 'mobilization_advance_pct', l: 'Mobilisation Advance', suffix: '% of contract', step: 0.5 },
  { k: 'material_advance_pct',     l: 'Material Advance',     suffix: '% of contract', step: 0.5 },
  { k: 'advance_bg_pct',           l: 'Advance BG',            suffix: '% of advance',  step: 1 },
  { k: 'performance_bg_pct',       l: 'Performance BG',        suffix: '% of contract', step: 0.5 },
  { k: 'retention_pct',            l: 'Retention',             suffix: '% of bill',     step: 0.5 },
  { k: 'dlp_months',               l: 'DLP',                   suffix: 'months',        step: 1 },
  { k: 'ra_certification_days',    l: 'RA Cert. Period',       suffix: 'days',          step: 1 },
  { k: 'bill_processing_days',     l: 'Bill Processing',       suffix: 'days',          step: 1 },
];

export function normalizePaymentTerms(input, fallback = DEFAULT_PAYMENT_TERMS) {
  const merged = { ...DEFAULT_PAYMENT_TERMS, ...(fallback || {}), ...(input || {}) };
  const out = {};
  for (const f of NUMERIC_FIELDS) {
    const raw = merged[f.k];
    const n = Number(raw);
    out[f.k] = Number.isFinite(n) ? n : DEFAULT_PAYMENT_TERMS[f.k];
  }
  out.ld_clause = (merged.ld_clause ?? '').toString();
  return out;
}

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none';

export default function BillingPaymentTermsFields({
  billingType,
  raBillFrequency,
  paymentTerms,
  onChange,
  billingTypes = ['Milestone Basis', 'RA Bill Basis'],
  raFrequencies = ['Weekly', 'Fortnightly', 'Monthly'],
  testIdPrefix = 'create',
}) {
  const pt = paymentTerms || {};

  const setBT = (bt) => onChange({ billingType: bt, raBillFrequency, paymentTerms: pt });
  const setFreq = (f) => onChange({ billingType, raBillFrequency: f, paymentTerms: pt });
  const setPT = (k, v) =>
    onChange({ billingType, raBillFrequency, paymentTerms: { ...pt, [k]: v } });

  return (
    <>
      {/* Billing type */}
      <div className="col-span-2">
        <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
          Client Billing Type
        </label>
        <div className="grid grid-cols-2 gap-2">
          {billingTypes.map((bt) => (
            <button
              key={bt}
              type="button"
              data-testid={`${testIdPrefix}-billing-type-${bt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              onClick={() => setBT(bt)}
              className={`px-3 py-2 text-xs font-display font-semibold border rounded-sm transition-all ${
                billingType === bt
                  ? 'border-[#3b4872] bg-indigo-50 text-[#3b4872]'
                  : 'border-slate-300 hover:border-slate-400 text-slate-600'
              }`}
            >
              {bt === 'Milestone Basis' ? 'Milestone Basis Payment' : 'RA Bill Basis Payment'}
            </button>
          ))}
        </div>
      </div>

      {billingType === 'RA Bill Basis' && (
        <div className="col-span-2">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
            RA Bill Frequency
          </label>
          <div className="grid grid-cols-3 gap-2">
            {raFrequencies.map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`${testIdPrefix}-ra-frequency-${f.toLowerCase()}`}
                onClick={() => setFreq(f)}
                className={`px-3 py-2 text-xs font-display font-semibold border rounded-sm transition-all ${
                  raBillFrequency === f
                    ? 'border-[#3b4872] bg-indigo-50 text-[#3b4872]'
                    : 'border-slate-300 hover:border-slate-400 text-slate-600'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Payment terms */}
      <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="kpi-overline">Payment Terms</div>
          <div className="text-[10px] text-slate-400 font-mono">advances · BG · DLP · certification · processing</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {NUMERIC_FIELDS.map((f) => (
            <div key={f.k}>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">
                {f.l}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  step={f.step}
                  value={pt[f.k] ?? ''}
                  onChange={(e) => setPT(f.k, e.target.value === '' ? '' : Number(e.target.value))}
                  data-testid={`${testIdPrefix}-pt-${f.k.replace(/_/g, '-')}`}
                  className={`${inputCls} tabular-nums pr-14`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-mono pointer-events-none">
                  {f.suffix}
                </span>
              </div>
            </div>
          ))}
          <div className="col-span-2 md:col-span-4">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">
              LD Clause
            </label>
            <input
              type="text"
              value={pt.ld_clause ?? ''}
              onChange={(e) => setPT('ld_clause', e.target.value)}
              data-testid={`${testIdPrefix}-pt-ld-clause`}
              className={inputCls}
              placeholder="e.g. 0.5% per week up to 10%"
            />
          </div>
        </div>
      </div>
    </>
  );
}
